import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  GetAllFollowUpTypes,
  fetchProjectData,
  GetLeadLog,
  fetchLeadData,
} from "../Services/APIService";
import Select from "react-select";
import toast, { Toaster } from "react-hot-toast";
import OverlayLoader from "./OverlayLoader";

interface EditUserTaskDemoProps {
  isOpen: boolean;
  onClose: () => void;
  taskData?: any;
  onSubmit?: (data: any) => void;
  onTaskUpdated?: () => void;
}

export interface followupTypeList {
  id: number;
  name: string;
  isActive: boolean;
}

export interface Project {
  projectID: number;
  projectName: string;
}

export interface Leads {
  leadId: number;
  firstName: string;
  lastName: string;
}

export interface LeadLgoList {
  logId: number;
  leadId: number;
  isNextActionRequired: boolean;
  taskId: number | null;
  assignedUser: number;
}

interface SelectOption {
  value: number;
  label: string;
  projectData?: Project;
}

interface SelectLeadOption {
  value: number;
  label: string;
  leadData: Leads;
}

interface FollowUpTypeOption {
  value: number;
  label: string;
}

interface FormData {
  reportTo: SelectOption | null;
  note: string;
  followupDate: string;
  leadData: SelectLeadOption | null;
  followUpType: FollowUpTypeOption | null;
  taskStatus: "active" | "postponed" | "completed" | "canceled";
  postponeReason: string;
  rescheduleDate: string;
  taskCompletionDate: string;
}

export interface UpdateUserTaskRequest {
  id: number;
  tenantId: number;
  leadId: number;
  projectId: number;
  assignedUser: number;
  followupTypeId: number;
  followupDate: string;
  taskNotes: string;
  isPostponed: boolean;
  postponeReason: string;
  rescheduleDate: string;
  isCompleted: boolean;
  isCanceled: boolean;
  taskCompletionDate: string;
  modifiedBy: number;
}

interface ValidationErrors {
  note?: string;
  followupDate?: string;
  leadData?: string;
  followUpType?: string;
}

const updateUserTask = async (requestData: UpdateUserTaskRequest) => {
  try {
    const accessToken = localStorage.getItem("authToken");
    const response = await fetch(
      "https://rprocrmsasdevapi.rprocrm.com/api/LeadAction/UpdateUserTask",
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestData),
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error("Error updating user task:", error);
    throw error;
  }
};

const EditUserTaskDemo: React.FC<EditUserTaskDemoProps> = ({
  isOpen,
  onClose,
  taskData,
  onSubmit,
  onTaskUpdated,
}) => {
  const [followupType, setFollowupType] = useState<followupTypeList[]>([]);
  const [projectData, setprojectData] = useState<Project[]>([]);
  const [projectOption, setProjectOption] = useState<SelectOption[]>([]);
  const [followUpTypeOption, setFollowUpTypeOption] = useState<FollowUpTypeOption[]>([]);
  const [allowCreateTaskLeadOption, setallowCreateTaskLeadOption] = useState<SelectLeadOption[]>([]);
  const [leadLogData, setLeadLogData] = useState<LeadLgoList[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [formData, setFormData] = useState<FormData>({
    reportTo: null,
    note: "",
    followupDate: "",
    leadData: null,
    followUpType: null,
    taskStatus: "active",
    postponeReason: "",
    rescheduleDate: "",
    taskCompletionDate: "",
  });
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const getFetchpMasteData = async () => {
      setIsLoading(true);

      try {
        // Fetch followup types
        const fetchAllFolowUpType = await GetAllFollowUpTypes();
        const activeTypes = fetchAllFolowUpType.filter(
          (type: followupTypeList) =>
            type.isActive &&
            type.id !== 0 &&
            type.name.toLowerCase() !== "no followup" &&
            type.name.toLowerCase() !== "note"
        );
        setFollowupType(activeTypes);

        const followUpOptions = activeTypes.map((type) => ({
          value: type.id,
          label: type.name,
        }));
        setFollowUpTypeOption(followUpOptions);

        // Fetch project
        const fetchAllProjectData = await fetchProjectData();
        const ActiveProjectData = fetchAllProjectData.filter(
          (project) => project.isActive === true
        );
        const options = ActiveProjectData.map((project) => ({
          value: project.projectID,
          label: project.projectName,
          projectData: project,
        }));
        setProjectOption(options);
        setprojectData(ActiveProjectData);

        // Fetch LeadLogData
        const leadLogs = await GetLeadLog();
        setLeadLogData(leadLogs);

        // Get all lead data
        const fetchAllLeadData = await fetchLeadData();
        const dropdownOptions = fetchAllLeadData.map((lead) => ({
          value: lead.leadId,
          label: `${lead.firstName} ${lead.lastName}`,
          leadData: lead,
        }));
        setallowCreateTaskLeadOption(dropdownOptions);

        // Populate form data if task data exists
        if (taskData) {
          populateFormWithTaskData(taskData, options, dropdownOptions, followUpOptions);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (isOpen) {
      getFetchpMasteData();
    }
  }, [isOpen, taskData]);

  const populateFormWithTaskData = (
    taskData: any,
    projectOptions: SelectOption[],
    leadOptions: SelectLeadOption[],
    followUpOptions: FollowUpTypeOption[]
  ) => {
    const selectedProject = projectOptions.find(
      (option) => option.value === taskData.projectId
    );
    const selectedLead = leadOptions.find(
      (option) => option.value === taskData.leadId
    );
    const selectedFollowUpType = followUpOptions.find(
      (option) => option.value === taskData.followupTypeId
    );

    const formatDateForInput = (dateString: string) => {
      if (!dateString) return "";
      const date = new Date(dateString);
      return formatDateTimeLocal(date);
    };

    let taskStatus: "active" | "postponed" | "completed" | "canceled" = "active";
    if (taskData.actionType === "postpone" || taskData.isPostponed) {
      taskStatus = "postponed";
    } else if (taskData.actionType === "complete" || taskData.isCompleted) {
      taskStatus = "completed";
    } else if (taskData.actionType === "cancel" || taskData.isCanceled) {
      taskStatus = "canceled";
    }

    setFormData({
      reportTo: selectedProject || null,
      leadData: selectedLead || null,
      followUpType: selectedFollowUpType || null,
      note: taskData.taskNotes || "",
      followupDate: formatDateForInput(taskData.followupDate),
      taskStatus: taskStatus,
      postponeReason: taskData.postponeReason || "",
      rescheduleDate: formatDateForInput(taskData.rescheduleDate),
      taskCompletionDate: formatDateForInput(taskData.taskCompletionDate),
    });
  };

  const handleSelectChange = (
    name: string,
    selectedOption: SelectOption | FollowUpTypeOption | SelectLeadOption | null
  ) => {
    setFormData((prev) => ({
      ...prev,
      [name]: selectedOption,
    }));
    if (selectedOption && errors[name as keyof ValidationErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (value && errors[name as keyof ValidationErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleTaskStatusChange = (
    status: "active" | "postponed" | "completed" | "canceled"
  ) => {
    setFormData((prev) => ({
      ...prev,
      taskStatus: status,
    }));
  };

  const getLocalStorageValue = (key: string): number => {
    const value = localStorage.getItem(key);
    return value ? parseInt(value, 10) : 0;
  };

  const validateForm = (): { isValid: boolean; errors: ValidationErrors } => {
    const newErrors: ValidationErrors = {};
    let isValid = true;

    if (!formData.followUpType) {
      newErrors.followUpType = "Follow-up type is required";
      isValid = false;
    }
    if (!formData.leadData) {
      newErrors.leadData = "Lead selection is required";
      isValid = false;
    }
    if (!formData.followupDate) {
      newErrors.followupDate = "Followup date is required";
      isValid = false;
    }
    if (!formData.note.trim()) {
      newErrors.note = "Task note is required";
      isValid = false;
    }

    setErrors(newErrors);
    return { isValid, errors: newErrors };
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      const validation = validateForm();
      if (!validation.isValid) {
        setErrors(validation.errors);
        console.error("Form validation failed:", validation.errors);
        return;
      }

      const tenantId = getLocalStorageValue("tenantId");
      const modifiedBy = getLocalStorageValue("UserId");
      const selectedLeadId = formData.leadData?.value || taskData?.leadId || 0;
      const selectedLeadLogData = leadLogData.find(
        (x) => x.leadId === selectedLeadId
      );

      const requestData: UpdateUserTaskRequest = {
        id: taskData?.id || 0,
        tenantId: tenantId,
        leadId: selectedLeadId,
        projectId: formData.reportTo?.value || 0,
        assignedUser: selectedLeadLogData?.assignedUser || taskData?.assignedUser || 0,
        followupTypeId: formData.followUpType?.value || 0,
        followupDate: formData.followupDate,
        taskNotes: formData.note,
        isPostponed: formData.taskStatus === "postponed",
        postponeReason: formData.postponeReason,
        rescheduleDate: formData.rescheduleDate || new Date().toISOString(),
        isCompleted: formData.taskStatus === "completed",
        isCanceled: formData.taskStatus === "canceled",
        taskCompletionDate: formData.taskCompletionDate || new Date().toISOString(),
        modifiedBy: modifiedBy,
      };

      const result = await updateUserTask(requestData);
      console.log("Task updated successfully:", result);

      toast.success("Task updated successfully!");

      setFormData({
        reportTo: null,
        note: "",
        followupDate: "",
        leadData: null,
        followUpType: null,
        taskStatus: "active",
        postponeReason: "",
        rescheduleDate: "",
        taskCompletionDate: "",
      });

      if (onSubmit) {
        onSubmit(requestData);
      }
      if (onTaskUpdated) {
        onTaskUpdated();
      }
      onClose();
    } catch (error) {
      console.error("Error updating task:", error);
      toast.error("Error updating task. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDateTimeLocal = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  useEffect(() => {
    if (!taskData && isOpen && !formData.followupDate) {
      const now = new Date();
      setFormData((prev) => ({
        ...prev,
        followupDate: formatDateTimeLocal(now),
      }));
    }
  }, [taskData, isOpen]);

  const handleClose = () => {
    setFormData({
      reportTo: null,
      note: "",
      followupDate: "",
      leadData: null,
      followUpType: null,
      taskStatus: "active",
      postponeReason: "",
      rescheduleDate: "",
      taskCompletionDate: "",
    });
    setErrors({});
    onClose();
  };

  const requiredField = <span className="text-danger ms-1">*</span>;

  return (
    <>
      <div>
        <Toaster position="top-center" reverseOrder={false} />
      </div>

      <div
        className={`modal-backdrop-custom ${isOpen ? "open" : "closed"}`}
        onClick={handleClose}
      ></div>

      <div className={`modal-custom ${isOpen ? "open" : "closed"}`}>
        <div className="modal-content usertaskCreatFormContent">
          <div className="modal-header-custom">
            <h5 className="modal-title-custom">Edit Follow-up</h5>
            <button
              type="button"
              className="btn-close-custom"
              onClick={handleClose}
            >
              <span className="icon-close"></span>
            </button>
          </div>

          <div className="modal-body-custom">
            <div className="card pop-up-card">
              <div className="card-body p-0 overflow-hidden">
                <div className="modal-body-content">
                  <form>
                    <div className="row">
                      <div className="col-12 col-lg-6 mb-3 input-group-custom">
                        <label className="form-label">
                          Follow-up Type{requiredField}
                        </label>
                        <Select
                          options={followUpTypeOption}
                          value={formData.followUpType}
                          onChange={(option) => handleSelectChange("followUpType", option)}
                          className={`basic-single-selectm ${
                            errors.followUpType ? "is-invalid" : ""
                          }`}
                          classNamePrefix="select"
                          placeholder="Select follow-up type"
                          isLoading={isLoading}
                          isClearable
                          isSearchable
                        />
                        {errors.followUpType && (
                          <div className="invalid-feedback-custom">
                            {errors.followUpType}
                          </div>
                        )}
                      </div>
                      <div className="col-12 col-lg-6 mb-3 input-group-custom">
                        <label className="form-label">Select Project</label>
                        <Select
                          options={projectOption}
                          value={formData.reportTo}
                          onChange={(option) => handleSelectChange("reportTo", option)}
                          className="basic-single-selectm"
                          classNamePrefix="select"
                          placeholder="Select project"
                          isLoading={isLoading}
                          isClearable
                          isSearchable
                        />
                      </div>
                      <div className="col-12 col-lg-6 mb-3 input-group-custom">
                        <label className="form-label">
                          Select Lead{requiredField}
                        </label>
                        <Select
                          options={allowCreateTaskLeadOption}
                          value={formData.leadData}
                          onChange={(option) => handleSelectChange("leadData", option)}
                          className={`basic-single-selectm ${
                            errors.leadData ? "is-invalid" : ""
                          }`}
                          classNamePrefix="select"
                          placeholder="Select Lead"
                          isLoading={isLoading}
                          isClearable
                          isSearchable
                        />
                        {errors.leadData && (
                          <div className="invalid-feedback-custom">
                            {errors.leadData}
                          </div>
                        )}
                      </div>
                      <div className="col-12 col-lg-6 mb-3 input-group-custom">
                        <label htmlFor="followupDate" className="form-label">
                          Followup Date{requiredField}
                        </label>
                        <div className="input-with-icon">
                          <input
                            type="datetime-local"
                            id="followupDate"
                            name="followupDate"
                            className={`form-control-custom form-control-custom1 ${
                              errors.followupDate ? "is-invalid" : ""
                            }`}
                            value={formData.followupDate}
                            onChange={handleInputChange}
                            required
                          />
                          {errors.followupDate && (
                            <div className="invalid-feedback-custom">
                              {errors.followupDate}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="col-12 mb-3 input-group-custom">
                        <label htmlFor="note" className="form-label">
                          Task Note{requiredField}
                        </label>
                        <div className="input-with-icon">
                          <span className="input-icon icon-note"></span>
                          <textarea
                            onChange={handleInputChange}
                            id="note"
                            name="note"
                            className={`form-control-custom-textarea ${
                              errors.note ? "is-invalid" : ""
                            }`}
                            placeholder="Enter Task Note"
                            value={formData.note}
                            rows={4}
                          />
                          {errors.note && (
                            <div className="invalid-feedback-custom">
                              {errors.note}
                            </div>
                          )}
                        </div>
                      </div>
                      {/* <div className="col-12 mb-3 input-group-custom">
                        <label className="form-label">Task Status</label>
                        <div className="row">
                          <div className="col-12 col-lg-3 mb-2">
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="radio"
                                id="taskStatusActive"
                                name="taskStatus"
                                value="active"
                                checked={formData.taskStatus === "active"}
                                onChange={() => handleTaskStatusChange("active")}
                              />
                              <label
                                className="form-check-label"
                                htmlFor="taskStatusActive"
                              >
                                Active
                              </label>
                            </div>
                          </div>
                          <div className="col-12 col-lg-3 mb-2">
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="radio"
                                id="taskStatusPostponed"
                                name="taskStatus"
                                value="postponed"
                                checked={formData.taskStatus === "postponed"}
                                onChange={() => handleTaskStatusChange("postponed")}
                              />
                              <label
                                className="form-check-label"
                                htmlFor="taskStatusPostponed"
                              >
                                <strong>Is Postponed</strong>
                              </label>
                            </div>
                          </div>
                          <div className="col-12 col-lg-3 mb-2">
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="radio"
                                id="taskStatusCompleted"
                                name="taskStatus"
                                value="completed"
                                checked={formData.taskStatus === "completed"}
                                onChange={() => handleTaskStatusChange("completed")}
                              />
                              <label
                                className="form-check-label"
                                htmlFor="taskStatusCompleted"
                              >
                                <strong>Is Completed</strong>
                              </label>
                            </div>
                          </div>
                          <div className="col-12 col-lg-3 mb-2">
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="radio"
                                id="taskStatusCanceled"
                                name="taskStatus"
                                value="canceled"
                                checked={formData.taskStatus === "canceled"}
                                onChange={() => handleTaskStatusChange("canceled")}
                              />
                              <label
                                className="form-check-label"
                                htmlFor="taskStatusCanceled"
                              >
                                <strong>Is Canceled</strong>
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                      {formData.taskStatus === "postponed" && (
                        <>
                          <div className="col-12 col-lg-6 mb-3 input-group-custom">
                            <label htmlFor="postponeReason" className="form-label">
                              Postpone Reason
                            </label>
                            <div className="input-with-icon">
                              <span className="input-icon icon-note"></span>
                              <input
                                type="text"
                                id="postponeReason"
                                name="postponeReason"
                                className="form-control-custom form-control-custom1"
                                placeholder="Enter postpone reason"
                                value={formData.postponeReason}
                                onChange={handleInputChange}
                              />
                            </div>
                          </div>
                          <div className="col-12 col-lg-6 mb-3 input-group-custom">
                            <label htmlFor="rescheduleDate" className="form-label">
                              Reschedule Date
                            </label>
                            <div className="input-with-icon">
                              <input
                                type="datetime-local"
                                id="rescheduleDate"
                                name="rescheduleDate"
                                className="form-control-custom form-control-custom1"
                                value={formData.rescheduleDate}
                                onChange={handleInputChange}
                              />
                            </div>
                          </div>
                        </>
                      )}
                      {formData.taskStatus === "completed" && (
                        <div className="col-12 col-lg-6 mb-3 input-group-custom">
                          <label htmlFor="taskCompletionDate" className="form-label">
                            Task Completion Date
                          </label>
                          <div className="input-with-icon">
                            <input
                              type="datetime-local"
                              id="taskCompletionDate"
                              name="taskCompletionDate"
                              className="form-control-custom form-control-custom1"
                              value={formData.taskCompletionDate}
                              onChange={handleInputChange}
                            />
                          </div>
                        </div>
                      )} */}
                    </div>
                  </form>
                </div>
              </div>
            </div>

            <div className="modal-footer-custom">
              <div>
                <button
                  type="button"
                  onClick={handleClose}
                  className="btn-custom btn-cancel"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-custom btn-primary-custom"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Updating..." : "Update Task"}
                </button>
              </div>
            </div>
          </div>
        </div>
        <OverlayLoader
          loading={isLoading}
          message="Loading User Task Data"
          submessage="This might take a moment..."
        />
      </div>
    </>
  );
};

export default EditUserTaskDemo;